import { useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { cx } from "./utils";

/** One segment. `value` is the generic payload returned by `onChange`. */
export interface SegmentedOption<T> {
  value: T;
  label: ReactNode;
  /** Text used for aria-label when `label` is an icon. */
  title?: string;
  disabled?: boolean;
}

export interface SegmentedControlProps<T> {
  options: ReadonlyArray<SegmentedOption<T>>;
  /** Selected value; compared with `Object.is`. */
  value: T | null;
  onChange: (value: T) => void;
  /** Accessible name for the whole group. */
  label: string;
  /** @default "md" */
  size?: "sm" | "md" | "lg";
  /** Stack segments vertically (mobile per-character forms). */
  vertical?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Radio-style single-select group (`role="radiogroup"`), keyboard-navigable with
 * arrow keys and Home/End. Generic over the option payload type.
 */
export function SegmentedControl<T>({
  options,
  value,
  onChange,
  label,
  size = "md",
  vertical = false,
  disabled = false,
  className,
}: SegmentedControlProps<T>) {
  const ref = useRef<HTMLDivElement>(null);

  const move = (from: number, dir: 1 | -1) => {
    const n = options.length;
    for (let i = 1; i <= n; i += 1) {
      const idx = (from + dir * i + n * n) % n;
      const opt = options[idx];
      if (opt && !opt.disabled) {
        onChange(opt.value);
        const btns = ref.current?.querySelectorAll<HTMLButtonElement>("button.xz-seg__item");
        btns?.[idx]?.focus();
        return;
      }
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>, idx: number) => {
    if (disabled) return;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      move(idx, 1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      move(idx, -1);
    } else if (e.key === "Home") {
      e.preventDefault();
      move(-1, 1);
    } else if (e.key === "End") {
      e.preventDefault();
      move(options.length, -1);
    }
  };

  const selectedIdx = options.findIndex((o) => Object.is(o.value, value));

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={label}
      className={cx(
        "xz-seg",
        `xz-seg--${size}`,
        vertical && "xz-seg--vertical",
        disabled && "is-disabled",
        className,
      )}
    >
      {options.map((o, i) => {
        const selected = i === selectedIdx;
        return (
          <button
            key={i}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={o.title}
            className={cx("xz-seg__item", selected && "is-selected")}
            disabled={disabled || o.disabled}
            tabIndex={selected || (selectedIdx === -1 && i === 0) ? 0 : -1}
            onKeyDown={(e) => onKeyDown(e, i)}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
